<template>
  <div class="site-form-container">
    <!-- Backdrop blur overlay -->
    <div class="form-backdrop"></div>

    <!-- Main Form Card -->
    <div class="form-card" :class="{ 'view-mode': !editable }">

      <!-- Header -->
      <header class="form-header">
        <div class="header-content">
          <!-- Logo & Title -->
          <div class="header-left">
            <div class="site-avatar" v-if="value.logo_url">
              <img :src="value.logo_url" alt="Logo" />
            </div>
            <div class="site-avatar placeholder" v-else>
              <span class="material-symbols-outlined">domain</span>
            </div>

            <div class="header-info">
              <h1 class="form-title">
                <template v-if="editable">
                  {{ action === 'create' ? 'Nouvel Univers' : 'Modifier l\'Univers' }}
                </template>
                <template v-else>{{ value.name || 'Détails de l\'Univers' }}</template>
              </h1>
              <p class="form-subtitle" v-if="!editable && value.client">
                <span class="material-symbols-outlined">business</span>
                {{ value.client.name }}
              </p>
            </div>
          </div>

          <!-- Header Actions -->
          <div class="header-actions">
            <button
                v-if="!editable"
                class="btn btn-icon btn-dashboard"
                :title="'Voir le Dashboard'"
                @click="goToDashboard"
            >
              <span class="material-symbols-outlined">bar_chart</span>
            </button>
            <button class="btn btn-ghost" @click="cancel">
              <span class="material-symbols-outlined">close</span>
              <span class="btn-text">{{ editable ? 'Annuler' : 'Fermer' }}</span>
            </button>
          </div>
        </div>

        <!-- Progress Indicator for Create Mode -->
        <div class="progress-bar" v-if="editable && action === 'create'">
          <div class="progress-fill" :style="{ width: formProgress + '%' }"></div>
        </div>
      </header>

      <!-- Form Content -->
      <v-form ref="form" class="form-body">

        <!-- Quick Edit Section (Name & Client) - Only in Edit Mode -->
        <section class="quick-edit-section" v-if="editable">
          <div class="input-grid">
            <div class="input-group name-input">
              <label class="input-label">
                <span class="material-symbols-outlined">edit</span>
                Nom de l'univers
              </label>
              <input
                  type="text"
                  v-model="value.name"
                  class="form-input"
                  placeholder="Entrez le nom..."
                  required
              />
            </div>

            <div class="input-group client-input">
              <label class="input-label">
                <span class="material-symbols-outlined">business</span>
                Client
              </label>
              <v-autocomplete
                  v-model="value.client_id"
                  :items="availableClients"
                  item-text="name"
                  item-value="id"
                  placeholder="Sélectionner un client"
                  solo
                  flat
                  hide-details
                  dense
                  class="custom-autocomplete"
                  @change="fetchClientSites"
              ></v-autocomplete>
            </div>
          </div>

          <!-- Duplicate Site Option -->
          <div class="duplicate-section" v-if="action === 'create'">
            <div class="duplicate-toggle" @click="showDuplicateOptions = !showDuplicateOptions">
              <span class="material-symbols-outlined">content_copy</span>
              <span>Copier depuis un univers existant</span>
              <span class="material-symbols-outlined toggle-icon" :class="{ rotated: showDuplicateOptions }">
                expand_more
              </span>
            </div>
            <transition name="slide-fade">
              <div class="duplicate-options" v-if="showDuplicateOptions">
                <v-select
                    :items="user.sites"
                    item-text="complete_name"
                    item-value="id"
                    return-object
                    clearable
                    solo
                    flat
                    hide-details
                    dense
                    placeholder="Sélectionner un univers à dupliquer"
                    v-model="duplicateSite"
                    @change="changeDuplicateSite"
                    class="custom-autocomplete"
                ></v-select>
              </div>
            </transition>
          </div>
        </section>

        <!-- Navigation Tabs -->
        <nav class="tabs-navigation">
          <button
              v-for="tab in visibleTabs"
              :key="tab.id"
              :class="['tab-btn', { active: activeTab === tab.id }]"
              @click="activeTab = tab.id"
          >
            <span class="material-symbols-outlined">{{ tab.icon }}</span>
            <span class="tab-label">{{ tab.label }}</span>
            <span class="tab-indicator" v-if="getTabStatus(tab.id)">
              <span class="material-symbols-outlined">check_circle</span>
            </span>
          </button>
        </nav>

        <!-- Tab Content -->
        <div class="tab-content">
          <transition name="tab-fade" mode="out-in">

            <!-- General Information Tab -->
            <section v-if="activeTab === 'general'" key="general" class="tab-panel">
              <div class="panel-grid">
                <!-- Left Column -->
                <div class="panel-column">
                  <div class="field-group">
                    <label class="field-label">Adresse</label>
                    <input
                        v-if="editable"
                        type="text"
                        v-model="value.address"
                        class="form-input"
                        placeholder="Adresse complète..."
                    />
                    <p class="field-value" v-else>{{ value.address || '—' }}</p>
                  </div>

                  <div class="field-group">
                    <label class="field-label">
                      <span class="material-symbols-outlined">location_on</span>
                      Coordonnées GPS
                    </label>
                    <input
                        v-if="editable"
                        type="text"
                        v-model="value.gps"
                        class="form-input"
                        placeholder="Ex: 44.8378, -0.5792"
                    />
                    <p class="field-value" v-else>{{ value.gps || '—' }}</p>
                  </div>

                  <div class="field-group">
                    <label class="field-label">Description</label>
                    <textarea
                        v-if="editable"
                        v-model="value.description"
                        class="form-textarea"
                        placeholder="Description de l'univers..."
                        rows="4"
                    ></textarea>
                    <p class="field-value description" v-else>{{ value.description || '—' }}</p>
                  </div>
                </div>

                <!-- Right Column -->
                <div class="panel-column">
                  <!-- Logo Upload -->
                  <div class="field-group logo-field" v-if="editable">
                    <label class="field-label">Logo</label>
                    <div class="logo-upload-area">
                      <div class="logo-preview" v-if="value.logo_url">
                        <img :src="value.logo_url" alt="Logo preview" />
                        <button class="remove-logo-btn" @click="deleteLogo" title="Supprimer">
                          <span class="material-symbols-outlined">close</span>
                        </button>
                      </div>
                      <label class="logo-dropzone" v-else>
                        <input
                            type="file"
                            @change="handleLogoChange"
                            accept="image/png,image/jpeg,image/gif"
                            hidden
                        />
                        <span class="material-symbols-outlined">add_photo_alternate</span>
                        <span>Ajouter un logo</span>
                      </label>
                    </div>
                  </div>

                  <!-- Installation Date -->
                  <div class="field-group">
                    <label class="field-label">
                      <span class="material-symbols-outlined">event</span>
                      Date d'installation
                    </label>
                    <div class="date-picker-wrapper" v-if="editable">
                      <v-menu
                          v-model="menu_installation_date"
                          :close-on-content-click="false"
                          transition="scale-transition"
                          offset-y
                          min-width="290px"
                      >
                        <template v-slot:activator="{ on }">
                          <div class="date-input" v-on="on">
                            <span class="material-symbols-outlined">calendar_today</span>
                            <span>{{ formattedInstallationDate || 'Sélectionner une date' }}</span>
                          </div>
                        </template>
                        <v-date-picker
                            v-model="value.installation_date"
                            @input="menu_installation_date = false"
                            locale="fr-FR"
                            color="#2d5a3d"
                        ></v-date-picker>
                      </v-menu>
                    </div>
                    <p class="field-value" v-else>
                      {{ value.installation_date ? formatDate(value.installation_date) : '—' }}
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <!-- Integrations Tab -->
            <section v-if="activeTab === 'integrations'" key="integrations" class="tab-panel">
              <div class="integration-flow">
                <!-- Step 1: Select Integration -->
                <div class="integration-step" :class="{ completed: value.integration }">
                  <div class="step-header">
                    <div class="step-number">1</div>
                    <div class="step-info">
                      <h3>Intégration</h3>
                      <p>Sélectionnez le type d'intégration</p>
                    </div>
                  </div>
                  <v-autocomplete
                      v-model="value.integration"
                      :items="availableInt"
                      item-text="name"
                      item-value="id"
                      return-object
                      solo
                      flat
                      hide-details
                      dense
                      clearable
                      placeholder="Choisir une intégration..."
                      class="custom-autocomplete"
                      @change="recheckAvailableSensors"
                  ></v-autocomplete>
                </div>

                <!-- Step 2: Select Sensor -->
                <transition name="slide-fade">
                  <div class="integration-step" v-if="value.integration" :class="{ completed: value.integration_sensor }">
                    <div class="step-header">
                      <div class="step-number">2</div>
                      <div class="step-info">
                        <h3>Capteur {{ value.integration.name }}</h3>
                        <p>Associez un capteur à cet univers</p>
                      </div>
                    </div>
                    <v-autocomplete
                        v-model="value.integration_sensor"
                        :items="availableIntSensors"
                        item-text="name"
                        item-value="id"
                        return-object
                        solo
                        flat
                        hide-details
                        dense
                        clearable
                        placeholder="Choisir un capteur..."
                        class="custom-autocomplete"
                        @change="autoFillFTP"
                    ></v-autocomplete>
                  </div>
                </transition>

                <!-- Empty State -->
                <div class="empty-integration" v-if="!value.integration && !editable">
                  <span class="material-symbols-outlined">integration_instructions</span>
                  <p>Aucune intégration configurée</p>
                </div>
              </div>
            </section>

            <!-- Relations Tab -->
            <section v-if="activeTab === 'relations'" key="relations" class="tab-panel">
              <div class="relations-grid">
                <!-- Type -->
                <div class="field-group full-width">
                  <label class="field-label">
                    <span class="material-symbols-outlined">category</span>
                    Type d'univers
                  </label>
                  <v-autocomplete
                      v-if="editable"
                      v-model="value.type"
                      :items="SITE_TYPES_VALUES"
                      solo
                      flat
                      hide-details
                      dense
                      clearable
                      placeholder="Sélectionner un type..."
                      class="custom-autocomplete"
                      :rules="[rules.required]"
                  ></v-autocomplete>
                  <div class="type-badge" v-else>{{ value.type || 'Non défini' }}</div>
                </div>

                <!-- Parents -->
                <div class="relation-card">
                  <div class="relation-header">
                    <span class="material-symbols-outlined">arrow_upward</span>
                    <h4>Univers Parents</h4>
                  </div>
                  <v-autocomplete
                      v-if="editable"
                      v-model="value.relations.parents"
                      :items="availableParents"
                      item-text="name"
                      multiple
                      chips
                      deletable-chips
                      small-chips
                      solo
                      flat
                      hide-details
                      dense
                      return-object
                      placeholder="Ajouter des parents..."
                      class="custom-autocomplete chips-input"
                  ></v-autocomplete>
                  <div class="relation-chips" v-else>
                    <span
                        v-for="site in value.relations.parents"
                        :key="site.id"
                        class="relation-chip"
                    >
                      {{ site.name }}
                    </span>
                    <span class="empty-text" v-if="!value.relations.parents || !value.relations.parents.length">Aucun parent</span>
                  </div>
                </div>

                <!-- Children -->
                <div class="relation-card">
                  <div class="relation-header">
                    <span class="material-symbols-outlined">arrow_downward</span>
                    <h4>Univers Enfants</h4>
                  </div>
                  <v-autocomplete
                      v-if="editable"
                      v-model="value.relations.children"
                      :items="availableChildren"
                      item-text="name"
                      multiple
                      chips
                      deletable-chips
                      small-chips
                      solo
                      flat
                      hide-details
                      dense
                      return-object
                      placeholder="Ajouter des enfants..."
                      class="custom-autocomplete chips-input"
                  ></v-autocomplete>
                  <div class="relation-chips" v-else>
                    <span
                        v-for="site in value.relations.children"
                        :key="site.id"
                        class="relation-chip"
                    >
                      {{ site.name }}
                    </span>
                    <span class="empty-text" v-if="!value.relations.children || !value.relations.children.length">Aucun enfant</span>
                  </div>
                </div>
              </div>
            </section>

            <!-- Options Tab -->
            <section v-if="activeTab === 'options'" key="options" class="tab-panel">
              <div class="options-list">
                <!-- Year Compare Option -->
                <div class="option-card" :class="{ enabled: value.is_allow_year_compare }">
                  <div class="option-content">
                    <div class="option-icon">
                      <span class="material-symbols-outlined">compare</span>
                    </div>
                    <div class="option-info">
                      <h4>Comparer les millésimes</h4>
                      <p>Permet de comparer les données sur plusieurs années</p>
                    </div>
                  </div>
                  <v-switch
                      v-model="value.is_allow_year_compare"
                      :true-value="1"
                      :false-value="0"
                      inset
                      hide-details
                      :disabled="!editable"
                      color="#2d5a3d"
                  ></v-switch>
                </div>

                <!-- Nearest Years (sub-option) -->
                <transition name="slide-fade">
                  <div class="option-card sub-option" v-if="value.is_allow_year_compare" :class="{ enabled: value.is_allow_nearest_years }">
                    <div class="option-content">
                      <div class="option-icon">
                        <span class="material-symbols-outlined">trending_up</span>
                      </div>
                      <div class="option-info">
                        <h4>Calculer la proximité des millésimes</h4>
                        <p>Analyse basée sur précipitations et températures</p>
                      </div>
                    </div>
                    <v-switch
                        v-model="value.is_allow_nearest_years"
                        :true-value="1"
                        :false-value="0"
                        inset
                        hide-details
                        :disabled="!editable"
                        color="#2d5a3d"
                    ></v-switch>
                  </div>
                </transition>

                <!-- Climate Parameters -->
                <transition name="slide-fade">
                  <div class="climate-params" v-if="value.is_allow_year_compare && value.is_allow_nearest_years">
                    <div class="params-grid">
                      <div class="param-field">
                        <label>Précipitations</label>
                        <v-select
                            :items="availableMesures"
                            item-value="csv_column"
                            item-text="csv_column"
                            solo
                            flat
                            hide-details
                            dense
                            v-model="value.precipitations"
                            placeholder="Sélectionner..."
                            class="custom-autocomplete"
                        ></v-select>
                      </div>
                      <div class="param-field">
                        <label>Température moyenne</label>
                        <v-select
                            :items="availableMesures"
                            item-value="csv_column"
                            item-text="csv_column"
                            solo
                            flat
                            hide-details
                            dense
                            v-model="value.tmoy"
                            placeholder="Sélectionner..."
                            class="custom-autocomplete"
                        ></v-select>
                      </div>
                      <div class="param-field">
                        <label>Température max</label>
                        <v-select
                            :items="availableMesures"
                            item-value="csv_column"
                            item-text="csv_column"
                            solo
                            flat
                            hide-details
                            dense
                            v-model="value.tmax"
                            placeholder="Sélectionner..."
                            class="custom-autocomplete"
                        ></v-select>
                      </div>
                    </div>
                  </div>
                </transition>

                <!-- Input Value Option -->
                <div class="option-card" :class="{ enabled: value.is_allow_input_value }">
                  <div class="option-content">
                    <div class="option-icon">
                      <span class="material-symbols-outlined">input</span>
                    </div>
                    <div class="option-info">
                      <h4>Saisir une information</h4>
                      <p>Permet d'ajouter des valeurs manuellement</p>
                    </div>
                  </div>
                  <v-switch
                      v-model="value.is_allow_input_value"
                      :true-value="1"
                      :false-value="0"
                      inset
                      hide-details
                      :disabled="!editable"
                      color="#2d5a3d"
                  ></v-switch>
                </div>
              </div>
            </section>

            <!-- Data Tab -->
            <section v-if="activeTab === 'data'" key="data" class="tab-panel">
              <div class="data-sections">
                <!-- Categories -->
                <div class="data-section">
                  <SiteCategories
                      :categories="value.categories.names"
                      :editable="editable"
                      @add="handleAddCategory"
                      @delete="handleDeleteCategory"
                  />
                </div>

                <!-- Sensors -->
                <div class="data-section sensors-section">
                  <div class="section-header">
                    <span class="material-symbols-outlined">sensors</span>
                    <h3>Mesures</h3>
                    <button
                        v-if="editable && action !== 'create'"
                        type="button"
                        class="import-sensors-btn"
                        @click="sensorImportOpen = true"
                    >
                      <span class="material-symbols-outlined">download</span>
                      Importation
                    </button>
                  </div>
                  <SiteSensors
                      :editable="editable"
                      :sensors="value.sensors"
                      @added="handleAddedSensorForm"
                      @deleted="handleDeletedSensor"
                  />
                </div>

                <!-- Events -->
                <div class="data-section">
                  <SiteEvents
                      :editable="editable"
                      :events="value.events.names"
                      @add="handleAddEvent"
                      @delete="handleDeleteEvent"
                  />
                </div>

                <!-- Calculation Formula -->
                <GlobalFormulaCalculator
                    v-if="editable"
                    v-model="value.calcForm"
                    :measures="value.sensors"
                />
                <div class="data-section formula-section" v-else>
                  <div class="section-header">
                    <span class="material-symbols-outlined">function</span>
                    <h3>Formule de calcul</h3>
                  </div>
                  <div class="formula-input-wrapper">
                    <code class="formula-display">{{ value.calcForm || 'Aucune formule' }}</code>
                  </div>
                </div>
              </div>

              <SensorImportDialog
                  :open="sensorImportOpen"
                  :site="value"
                  :clientSites="clientSites"
                  @imported="handleImportedSensors"
                  @close="sensorImportOpen = false"
              />
            </section>

            <!-- Timestamp Tab -->
            <section v-if="activeTab === 'timestamp'" key="timestamp" class="tab-panel">
              <div class="timestamp-config">
                <div class="timestamp-defaults">
                  <div class="field-group">
                    <label class="field-label">
                      <span class="material-symbols-outlined">schedule</span>
                      Format d'horodatage par défaut
                    </label>
                    <input
                        type="text"
                        v-model="value.date_formats.DefaultFormat"
                        class="form-input"
                        placeholder="Ex: YYYY-MM-DD HH:mm:ss"
                    />
                  </div>

                  <div class="field-group">
                    <label class="field-label">
                      <span class="material-symbols-outlined">description</span>
                      Format d'horodatage des fichiers
                    </label>
                    <input
                        type="text"
                        v-model="value.date_formats.FileFormat"
                        class="form-input"
                        placeholder="Ex: YYYYMMDD"
                    />
                  </div>
                </div>

                <!-- Overrides Table -->
                <div class="overrides-section">
                  <div class="overrides-header">
                    <h3>
                      <span class="material-symbols-outlined">tune</span>
                      Overrides
                    </h3>
                    <button class="btn btn-sm btn-primary" @click="addOverride" v-if="editable">
                      <span class="material-symbols-outlined">add</span>
                      Ajouter
                    </button>
                  </div>

                  <div class="overrides-table" v-if="value.date_formats.Overrides && value.date_formats.Overrides.length">
                    <div class="override-row header">
                      <span>Depuis</span>
                      <span>Jusqu'à</span>
                      <span>Format</span>
                      <span></span>
                    </div>
                    <div
                        class="override-row"
                        v-for="(item, index) in value.date_formats.Overrides"
                        :key="index"
                    >
                      <input type="text" v-model="item.from" placeholder="Date début" class="form-input sm" />
                      <input type="text" v-model="item.to" placeholder="Date fin" class="form-input sm" />
                      <input type="text" v-model="item.Format" placeholder="Format" class="form-input sm" />
                      <button class="btn btn-icon btn-danger-ghost" @click="removeOverride(index)" v-if="editable">
                        <span class="material-symbols-outlined">delete</span>
                      </button>
                    </div>
                  </div>

                  <div class="empty-overrides" v-else>
                    <span class="material-symbols-outlined">layers_clear</span>
                    <p>Aucun override configuré</p>
                  </div>
                </div>
              </div>
            </section>

            <!-- Users Tab -->
            <section v-if="activeTab === 'users'" key="users" class="tab-panel">
              <div class="users-section">
                <div class="users-selector" v-if="editable">
                  <v-autocomplete
                      v-model="value.users"
                      :items="availableUsers"
                      item-text="complete_name"
                      item-value="id"
                      multiple
                      chips
                      deletable-chips
                      small-chips
                      solo
                      flat
                      hide-details
                      dense
                      return-object
                      placeholder="Sélectionner les utilisateurs..."
                      class="custom-autocomplete chips-input"
                  ></v-autocomplete>

                  <button class="btn btn-outline" @click="createUser">
                    <span class="material-symbols-outlined">person_add</span>
                    Nouvel utilisateur
                  </button>
                </div>

                <!-- Users Grid -->
                <div class="users-grid" v-if="value.users && value.users.length">
                  <div class="user-card" v-for="user in value.users" :key="user.id">
                    <div class="user-avatar">
                      {{ getUserInitials(user) }}
                    </div>
                    <div class="user-info">
                      <span class="user-name">{{ user.complete_name }}</span>
                      <span class="user-role">{{ user.role }}</span>
                    </div>
                  </div>
                </div>

                <div class="empty-users" v-else>
                  <span class="material-symbols-outlined">group_off</span>
                  <p>Aucun utilisateur assigné</p>
                </div>
              </div>

              <!-- User Create Dialog -->
              <v-dialog v-model="createUserDialogVisible" max-width="1000px" persistent scrollable>
                <UserForm
                    ref="UserForm"
                    v-model="editedUser"
                    :showEditSites="false"
                    action="create"
                    @savedForm="handleSavedUserForm"
                    @canceledForm="handleCanceledUserForm"
                />
              </v-dialog>
            </section>

            <!-- CO2 Security Tab -->
            <section v-if="activeTab === 'co2'" key="co2" class="tab-panel">
              <div class="co2-config">
                <!-- Main Toggle -->
                <div class="option-card large" :class="{ enabled: value.is_securityCO2 }">
                  <div class="option-content">
                    <div class="option-icon warning">
                      <span class="material-symbols-outlined">co2</span>
                    </div>
                    <div class="option-info">
                      <h4>Sécurité CO2</h4>
                      <p>Activer le monitoring de sécurité CO2 pour cet univers</p>
                    </div>
                  </div>
                  <v-switch
                      v-model="value.is_securityCO2"
                      inset
                      hide-details
                      color="#d97706"
                  ></v-switch>
                </div>

                <transition name="slide-fade">
                  <div class="co2-details" v-if="value.is_securityCO2">
                    <!-- Plan Option -->
                    <div class="option-card" :class="{ enabled: value.use_securityCO2_plan }">
                      <div class="option-content">
                        <div class="option-icon">
                          <span class="material-symbols-outlined">map</span>
                        </div>
                        <div class="option-info">
                          <h4>Utiliser un plan</h4>
                          <p>Afficher les capteurs sur un plan interactif</p>
                        </div>
                      </div>
                      <v-switch
                          v-model="value.use_securityCO2_plan"
                          inset
                          hide-details
                          color="#2d5a3d"
                      ></v-switch>
                    </div>

                    <!-- Sensor Selection -->
                    <div class="field-group">
                      <label class="field-label">Mesures concernées</label>
                      <v-autocomplete
                          :items="value.sensors"
                          item-text="name"
                          item-value="csv_column"
                          chips
                          deletable-chips
                          small-chips
                          return-object
                          multiple
                          solo
                          flat
                          hide-details
                          dense
                          v-model="value.securityCO2_mesure"
                          placeholder="Sélectionner les mesures..."
                          class="custom-autocomplete chips-input"
                      ></v-autocomplete>
                    </div>

                    <!-- Sensor Configuration Table -->
                    <div class="sensor-config-table" v-if="value.securityCO2_mesure && value.securityCO2_mesure.length">
                      <div class="table-header">
                        <span>Mesure</span>
                        <span>Nom d'affichage</span>
                        <span v-if="value.use_securityCO2_plan">Position X</span>
                        <span v-if="value.use_securityCO2_plan">Position Y</span>
                      </div>
                      <div
                          class="table-row"
                          v-for="(sensor, idx) in value.securityCO2_mesure"
                          :key="idx"
                      >
                        <span class="sensor-name">{{ sensor.name }}</span>
                        <input
                            type="text"
                            v-model="sensor.display_name"
                            class="form-input sm"
                            placeholder="Nom affiché"
                        />
                        <input
                            v-if="value.use_securityCO2_plan"
                            type="number"
                            v-model="sensor.X"
                            class="form-input sm"
                            placeholder="X (px)"
                        />
                        <input
                            v-if="value.use_securityCO2_plan"
                            type="number"
                            v-model="sensor.Y"
                            class="form-input sm"
                            placeholder="Y (px)"
                        />
                      </div>
                    </div>
                  </div>
                </transition>
              </div>
            </section>

            <!-- Alerts Tab -->
            <section v-if="activeTab === 'alerts'" key="alerts" class="tab-panel">
              <template v-if="action !== 'create'">
                <SiteAlertPreferences
                    :site="value"
                    :editable="editable"
                />
              </template>
              <div class="ftp-notice" v-else>
                <span class="material-symbols-outlined">info</span>
                <div>
                  <h4>Alertes par utilisateur</h4>
                  <p>Vous pourrez régler les abonnements et les seuils personnels une fois l'univers enregistré.</p>
                </div>
              </div>
            </section>

            <!-- FTP Tab -->
            <section v-if="activeTab === 'ftp'" key="ftp" class="tab-panel">
              <div class="ftp-config">
                <template v-if="action !== 'create'">
                  <SiteFtpMode
                      ref="SiteFtpMode"
                      v-model="value"
                      :editable="editable"
                  />
                </template>
                <div class="ftp-notice" v-else>
                  <span class="material-symbols-outlined">info</span>
                  <div>
                    <h4>Configuration FTP</h4>
                    <p>Vous pourrez configurer les paramètres FTP une fois l'univers enregistré.</p>
                  </div>
                </div>
              </div>
            </section>

          </transition>
        </div>
      </v-form>

      <!-- Footer Actions -->
      <footer class="form-footer">
        <div class="footer-content">
          <div class="footer-left">
            <span class="save-hint" v-if="editable">
              <span class="material-symbols-outlined">keyboard</span>
              Ctrl+S pour sauvegarder
            </span>
          </div>
          <div class="footer-actions">
            <button class="btn btn-ghost" @click="cancel">
              {{ editable ? 'Annuler' : 'Fermer' }}
            </button>
            <button
                v-if="!editable && canModify()"
                class="btn btn-primary"
                @click="edit"
            >
              <span class="material-symbols-outlined">edit</span>
              Modifier
            </button>
            <button
                v-if="editable"
                class="btn btn-primary"
                @click="save"
                :disabled="loading"
                :class="{ loading }"
            >
              <span class="material-symbols-outlined" v-if="!loading">save</span>
              <span class="loader-spinner" v-else></span>
              {{ loading ? 'Sauvegarde...' : 'Sauvegarder' }}
            </button>
          </div>
        </div>
      </footer>
    </div>
  </div>
</template>

<script>
import UserForm from "../components/UserForm";
import SiteFtpMode from "../components/SiteFtpMode";
import SiteSensors from "../components/SiteSensors";
import SiteCategories from "../components/SiteCategories";
import SiteAlertPreferences from "../components/SiteAlertPreferences";
import SensorImportDialog from "../components/SensorImportDialog";
import SiteEvents from "../components/SiteEvents";
import GlobalFormulaCalculator from "@/components/GlobalFormulaCalculator";
import { buildSaveSiteRequest } from "@/vuex/modules/site";
import { SITE_TYPES_VALUES, DEFAULT_SITE_TYPE } from "@/const/sitetypes";
import { getSitePromise } from "@/vuex/modules/site";

export default {
  name: 'SiteForm',

  components: {
    UserForm,
    SiteFtpMode,
    SiteSensors,
    SiteCategories,
    SiteAlertPreferences,
    SensorImportDialog,
    SiteEvents,
    GlobalFormulaCalculator
  },

  props: {
    value: Object,
    action: {
      type: String,
      default: "update",
    },
    editable: {
      type: Boolean,
      default: false
    },
  },

  data() {
    const user = this.$store.state.profile.user;
    this.initSiteEmptyData();

    return {
      user,
      loading: false,
      activeTab: 'general',
      sensorImportOpen: false,
      showDuplicateOptions: false,
      duplicateSite: null,

      // Tab definitions
      tabs: [
        { id: 'general', label: 'Général', icon: 'info' },
        { id: 'integrations', label: 'Intégrations', icon: 'integration_instructions' },
        { id: 'relations', label: 'Relations', icon: 'account_tree' },
        { id: 'options', label: 'Options', icon: 'settings', adminOnly: true },
        { id: 'data', label: 'Données', icon: 'database' },
        { id: 'timestamp', label: 'Horodatage', icon: 'schedule' },
        { id: 'users', label: 'Utilisateurs', icon: 'group' },
        { id: 'co2', label: 'Sécurité CO2', icon: 'co2' },
        { id: 'alerts', label: 'Alertes', icon: 'notifications_active' },
        { id: 'ftp', label: 'FTP', icon: 'folder_shared', hideWhenIntSensor: true },
      ],

      // Validation rules
      rules: {
        required: (v) => !!v || "Champ requis",
        image: (v) => !v || ['image/jpeg', 'image/png', 'image/gif'].includes(v.type) || "Format image requis (.jpg, .png, .gif)",
      },

      // Data
      availableUsers: [],
      availableClients: [],
      availableInt: [],
      availableIntSensors: [],
      availableMesures: [],
      clientSites: [],
      childIdsToName: null,

      // Dialogs
      createUserDialogVisible: false,
      editedUser: {},
      defaultUser: {
        lastname: "",
        firstname: "",
        email: "",
        role: "",
        sites: [],
      },

      // Date picker
      menu_installation_date: false,

      // Constants
      SITE_TYPES_VALUES,
    };
  },

  computed: {
    visibleTabs() {
      return this.tabs.filter(tab => {
        if (tab.adminOnly && !['admin', 'user_admin'].includes(this.user.role)) return false;
        if (tab.hideWhenIntSensor && this.value.intSensor) return false;
        return true;
      });
    },

    formProgress() {
      let completed = 0;
      if (this.value.name) completed += 20;
      if (this.value.client_id) completed += 20;
      if (this.value.type) completed += 20;
      if (this.value.address) completed += 15;
      if (this.value.sensors && this.value.sensors.length) completed += 25;
      return Math.min(completed, 100);
    },

    formattedInstallationDate() {
      return this.value.installation_date
          ? this.$moment(this.value.installation_date).format("DD/MM/YYYY")
          : "";
    },

    availableParents() {
      return [...this.clientSites].filter(site =>
          this.value.id !== site.id &&
          !(this.value.relations && this.value.relations.children && this.value.relations.children.map(i => i.id).includes(site.id))
      );
    },

    availableChildren() {
      return [...this.clientSites].filter(site =>
          this.value.id !== site.id &&
          !(this.value.relations && this.value.relations.parents && this.value.relations.parents.map(i => i.id).includes(site.id))
      );
    },
  },

  watch: {
    value: {
      handler() {
        if (this.$refs.form) this.$refs.form.resetValidation();
        this.initSiteEmptyData();
      },
      deep: true,
    },
    createUserDialogVisible(val) {
      if (val && this.$resetDialogScroll) this.$resetDialogScroll();
    },
  },

  async mounted() {
    this.initSiteEmptyData();
    this.fetchUsers();
    this.fetchClients();
    this.fetchClientSites();
    this.fetchIntConfigs();
    this.childIdsToName = await this.getChildIdsToName();
    this.availableMesures = await this.getAvailableMesures();

    // Parse JSON fields
    this.value.securityCO2_mesure = this.value.securityCO2_mesure
        ? JSON.parse(this.value.securityCO2_mesure)
        : [];

    this.value.date_formats = (this.value.date_formats && this.value.date_formats !== "null")
        ? JSON.parse(this.value.date_formats)
        : { DefaultFormat: '', FileFormat: '', Overrides: [] };

    // Keyboard shortcuts
    document.addEventListener('keydown', this.handleKeydown);
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydown);
  },

  methods: {
    // Keyboard shortcuts
    handleKeydown(e) {
      if ((e.ctrlKey || e.metaKey) && e.key === 's' && this.editable) {
        e.preventDefault();
        this.save();
      }
    },

    // Navigation
    goToDashboard() {
      this.$router.push({ name: 'Dashboard', params: { site_id: this.value.id } });
    },

    // Tab status
    getTabStatus(tabId) {
      switch(tabId) {
        case 'general': return this.value.address && this.value.gps;
        case 'integrations': return this.value.integration;
        case 'relations': return this.value.type;
        case 'data': return this.value.sensors && this.value.sensors.length;
        case 'users': return this.value.users && this.value.users.length > 1;
        default: return false;
      }
    },

    // Format date
    formatDate(date) {
      return this.$moment(date).format("D MMMM YYYY");
    },

    // User utilities
    getUserInitials(user) {
      const name = user.complete_name || '';
      const parts = name.split(' ');
      return parts.map(p => p[0]).join('').toUpperCase().slice(0, 2);
    },

    canModify() {
      return ['user_admin', 'admin'].includes(this.user.role);
    },

    // Initialize empty data
    initSiteEmptyData() {
      if (!this.value.relations) this.value.relations = {};
      if (!this.value.relations.children) this.value.relations.children = [];
      if (!this.value.relations.parents) this.value.relations.parents = [];
      if (!this.value.users || !this.value.users.length) this.value.users = [this.$store.state.profile.user];
      if (!this.value.type) this.value.type = DEFAULT_SITE_TYPE;
    },

    // Error handling
    showErr(err) {
      this.$store.commit("setSnack", { type: "red", err });
    },

    // Overrides
    addOverride() {
      this.value.date_formats.Overrides.push({ from: '', to: '', Format: '' });
    },

    removeOverride(index) {
      this.value.date_formats.Overrides.splice(index, 1);
    },

    // Child site helpers
    async getChildIdsToName() {
      const map = {};
      if (this.value.relations && this.value.relations.children) {
        for (const child of this.value.relations.children) {
          const site = await this.getChildSite(child.id);
          map[child.id] = site.name;
        }
      }
      return map;
    },

    async getChildSite(id) {
      const filtered = this.clientSites.filter(s => s.id === id);
      if (filtered.length === 1) return filtered[0];
      if (this.user.role === 'admin') return getSitePromise(this.$store, id);
      return null;
    },

    async getAvailableMesures() {
      let mesures = [...(this.value.sensors || [])];
      if (this.childIdsToName) {
        for (const childId of Object.keys(this.childIdsToName)) {
          const site = await this.getChildSite(childId);
          if (site && site.sensors) mesures = [...mesures, ...site.sensors];
        }
      }
      return mesures;
    },

    // Duplicate site
    changeDuplicateSite() {
      if (!this.duplicateSite) return;

      const src = this.duplicateSite;
      this.value.client_id = src.client_id;
      this.value.type = src.type;
      this.value.is_allow_year_compare = src.is_allow_year_compare;
      this.value.is_allow_input_value = src.is_allow_input_value;
      this.value.logo = src.logo;
      this.value.logo_url = src.logo_url;
      this.value.users = src.users;

      this.value.sensors = src.sensors.map(s => ({
        name: s.name,
        csv_column: s.csv_column,
        unit_of_measure: s.unit_of_measure,
      }));

      this.value.visualizations = src.visualizations.map(v => ({
        axes: v.axes,
        name: v.name,
      }));

      this.value.events = src.events;
      if (src.relations) this.value.relations = { ...src.relations };

      this.fetchClientSites();
    },

    // Save
    saveSitePromise(site) {
      return new Promise((resolve, reject) => {
        const build = buildSaveSiteRequest(site, this.action !== "update");
        this.$store.dispatch(build.request, build.queries)
            .then(site => {
              this.$store.commit("setSnack", { type: "success", msg: "Univers enregistré !" });
              resolve(site);
            })
            .catch(err => reject(err));
      });
    },

    async save() {
      if (!this.$refs.form.validate()) return;

      this.loading = true;
      try {
        const site = await this.saveSitePromise(this.value);
        this.$refs.form.resetValidation();
        this.$emit("savedForm", site);
      } catch (err) {
        this.saveErr(err);
      } finally {
        this.loading = false;
      }
    },

    saveErr(err) {
      const msg = (err.response && err.response.status !== 403) ? err.response.data : err;
      this.showErr(msg);
    },

    cancel() {
      if (this.$refs.form) this.$refs.form.resetValidation();
      this.$emit("canceledForm");
    },

    edit() {
      this.$emit("editForm");
    },

    // Logo
    handleLogoChange(e) {
      const file = e.target.files[0];
      if (file) {
        this.value.logo_url = URL.createObjectURL(file);
        this.value.logo = file;
        this.$forceUpdate();
      }
    },

    deleteLogo() {
      this.value.logo_url = null;
      this.value.logo = null;
      this.value.remove_logo = true;
      this.$forceUpdate();
    },

    // Fetch data
    fetchUsers() {
      if (this.user.role === 'admin') {
        this.$store.dispatch("usersRequest", {})
            .then(r => this.availableUsers = r.data)
            .catch(this.showErr);
      } else {
        this.availableUsers = [...this.user.users, this.user, ...(this.value.users || [])];
      }
    },

    fetchClients() {
      if (this.user.role === 'admin') {
        this.$store.dispatch("clientsRequest", {})
            .then(r => this.availableClients = r.data)
            .catch(this.showErr);
      } else {
        this.availableClients = [...this.user.clients];
      }
    },

    fetchClientSites() {
      if (this.user.role === 'admin') {
        this.$store.dispatch("sitesRequest", {})
            .then(r => this.clientSites = r.data.filter(s => s.client_id === this.value.client_id))
            .catch(this.showErr);
      } else {
        this.clientSites = this.user.sites.filter(s => s.client_id === this.value.client_id);
      }
    },

    fetchIntConfigs() {
      this.$store.dispatch('integrationsRequest', {})
          .then(result => {
            this.availableInt = result;
            if (this.value.integration) {
              this.recheckAvailableSensors();
            }
          })
          .catch(() => {});
    },

    recheckAvailableSensors() {
      this.availableIntSensors = [];
      if (!this.value.integration || !this.value.integration.integration_confs) return;

      this.value.integration.integration_confs.forEach(conf => {
        conf.integration_sensors.forEach(sensor => {
          this.availableIntSensors.push(sensor);
        });
      });

      this.availableIntSensors = this.availableIntSensors.filter(s =>
          (s.client_id === this.value.client_id && s.client_id !== null) || s.is_allowed_for_demo
      );
    },

    autoFillData() {
      if (this.value.integration && this.value.integration.csv_data) {
        this.value.sensors = JSON.parse(this.value.integration.csv_data);
      }
    },

    autoFillFTP() {
      if (this.value.integration_sensor) {
        this.value.ftp_host = "172.20.6.12";
        this.value.ftp_user = "mtp";
        this.value.ftp_pwd = "WelcomeToWinery";
        this.value.ftp_path = `/${this.value.integration.name}/${this.value.integration_sensor.name}/`;
        this.autoFillData();
      }
    },

    // User management
    createUser() {
      this.editedUser = { ...this.defaultUser };
      this.createUserDialogVisible = true;
    },

    handleSavedUserForm() {
      this.fetchUsers();
      this.createUserDialogVisible = false;
    },

    handleCanceledUserForm() {
      this.createUserDialogVisible = false;
    },

    // Sensor handlers
    /**
     * Mesures recopiees depuis un autre univers : elles rejoignent le
     * formulaire et ne seront ecrites qu'a la sauvegarde.
     */
    handleImportedSensors(sensors) {
      if (!this.value.sensors) this.value.sensors = [];

      for (const sensor of sensors) {
        this.value.sensors.push(sensor);
      }

      this.$forceUpdate();

      this.$store.commit("setSnack", {
        type: "green",
        err: `${sensors.length} mesure(s) ajoutée(s). Enregistrez l'univers pour les conserver.`,
      });
    },

    handleAddedSensorForm(sensor) {
      if (!this.value.sensors) this.value.sensors = [];
      this.value.sensors.push(sensor);
      this.$forceUpdate();
    },

    handleDeletedSensor(sensor) {
      this.value.sensors = this.value.sensors.filter(s => s !== sensor);
      this.$forceUpdate();
    },

    // Event handlers
    handleAddEvent(event) {
      this.value.events.names.push(event);
    },

    handleDeleteEvent(event) {
      this.value.events.names = this.value.events.names.filter(e => e !== event);
    },

    // Category handlers
    handleAddCategory(category) {
      this.value.categories.names.push(category);
    },

    handleDeleteCategory(category) {
      this.value.categories.names = this.value.categories.names.filter(c => c !== category);
    },
  },
};
</script>

<style scoped>
.import-sensors-btn {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #fff;
  border: 1px solid #2d5a3d;
  color: #2d5a3d;
  border-radius: 8px;
  padding: 5px 13px;
  font-size: 0.78rem;
  font-weight: 500;
  cursor: pointer;
}

.import-sensors-btn:hover {
  background: #eef5f0;
}

.import-sensors-btn .material-symbols-outlined {
  font-size: 16px;
}

/* ============================================
   CSS VARIABLES & THEME
   ============================================ */
.site-form-container {
  --primary: #2d5a3d;
  --primary-light: #3d7a52;
  --primary-lighter: #e8f0eb;
  --primary-dark: #1e3d29;
  --accent: #5a9e6f;
  --surface: #ffffff;
  --surface-elevated: #fafbfa;
  --border: #e2e8e4;
  --border-light: #f0f4f1;
  --text-primary: #1a2e1f;
  --text-secondary: #5a6b5e;
  --text-muted: #8a998e;
  --danger: #dc2626;
  --danger-light: #fef2f2;
  --warning: #d97706;
  --warning-light: #fffbeb;
  --success: #059669;
  --shadow-sm: 0 1px 2px rgba(45, 90, 61, 0.05);
  --shadow-md: 0 4px 12px rgba(45, 90, 61, 0.08);
  --shadow-lg: 0 12px 40px rgba(45, 90, 61, 0.12);
  --radius-sm: 6px;
  --radius-md: 10px;
  --radius-lg: 16px;
  --transition: 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  position: relative;
  width: 100%;
  min-height: 100%;
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* ============================================
   FORM CARD
   ============================================ */
.form-card {
  position: relative;
  background: var(--surface);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}

/* ============================================
   HEADER
   ============================================ */
.form-header {
  background: linear-gradient(135deg, var(--primary) 0%, var(--primary-light) 100%);
  padding: 0;
  flex-shrink: 0;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 28px;
  gap: 20px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.site-avatar {
  width: 56px;
  height: 56px;
  border-radius: var(--radius-md);
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}

.site-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.site-avatar.placeholder .material-symbols-outlined {
  font-size: 28px;
  color: rgba(255, 255, 255, 0.8);
}

.header-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.form-title {
  font-size: 1.35rem;
  font-weight: 600;
  color: white;
  margin: 0;
  letter-spacing: -0.02em;
}

.form-subtitle {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.75);
}

.form-subtitle .material-symbols-outlined {
  font-size: 16px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.btn-dashboard {
  background: rgba(255, 255, 255, 0.15) !important;
  color: white !important;
}

.btn-dashboard:hover {
  background: rgba(255, 255, 255, 0.25) !important;
}

/* Progress Bar */
.progress-bar {
  height: 3px;
  background: rgba(0, 0, 0, 0.1);
}

.progress-fill {
  height: 100%;
  background: rgba(255, 255, 255, 0.9);
  transition: width 0.4s ease;
}

/* ============================================
   BUTTONS
   ============================================ */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: var(--radius-sm);
  font-size: 0.875rem;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: var(--transition);
  white-space: nowrap;
}

.btn .material-symbols-outlined {
  font-size: 18px;
}

.btn-primary {
  background: var(--primary);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: var(--primary-light);
  transform: translateY(-1px);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary.loading {
  pointer-events: none;
}

.btn-ghost {
  background: rgba(255, 255, 255, 0.15);
  color: white;
  backdrop-filter: blur(4px);
}

.btn-ghost:hover {
  background: rgba(255, 255, 255, 0.25);
}

.btn-outline {
  background: transparent;
  border: 1px solid var(--border);
  color: var(--text-primary);
}

.btn-outline:hover {
  background: var(--surface-elevated);
  border-color: var(--primary);
}

.btn-icon {
  padding: 10px;
}

.btn-sm {
  padding: 8px 14px;
  font-size: 0.8125rem;
}

.btn-danger-ghost {
  color: var(--danger);
  background: transparent;
  padding: 6px;
}

.btn-danger-ghost:hover {
  background: var(--danger-light);
}

.btn-text {
  display: none;
}

@media (min-width: 640px) {
  .btn-text {
    display: inline;
  }
}

/* Loader Spinner */
.loader-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ============================================
   FORM BODY
   ============================================ */
.form-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

/* ============================================
   QUICK EDIT SECTION
   ============================================ */
.quick-edit-section {
  padding: 24px 28px;
  background: var(--surface-elevated);
  border-bottom: 1px solid var(--border-light);
}

.input-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}

@media (min-width: 768px) {
  .input-grid {
    grid-template-columns: 1fr 1fr;
  }
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.input-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--text-secondary);
}

.input-label .material-symbols-outlined {
  font-size: 16px;
  color: var(--accent);
}

.form-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  font-size: 0.9375rem;
  color: var(--text-primary);
  background: var(--surface);
  transition: var(--transition);
}

.form-input:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px var(--primary-lighter);
}

.form-input::placeholder {
  color: var(--text-muted);
}

.form-input.sm {
  padding: 8px 12px;
  font-size: 0.875rem;
}

.form-textarea {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  font-size: 0.9375rem;
  color: var(--text-primary);
  background: var(--surface);
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
  transition: var(--transition);
}

.form-textarea:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px var(--primary-lighter);
}

/* Duplicate Section */
.duplicate-section {
  margin-top: 16px;
}

.duplicate-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: var(--surface);
  border: 1px dashed var(--border);
  border-radius: var(--radius-sm);
  cursor: pointer;
  font-size: 0.875rem;
  color: var(--text-secondary);
  transition: var(--transition);
}

.duplicate-toggle:hover {
  border-color: var(--primary);
  color: var(--primary);
}

.duplicate-toggle .material-symbols-outlined {
  font-size: 18px;
}

.toggle-icon {
  margin-left: auto;
  transition: transform 0.2s;
}

.toggle-icon.rotated {
  transform: rotate(180deg);
}

.duplicate-options {
  margin-top: 12px;
}

/* ============================================
   TABS NAVIGATION
   ============================================ */
.tabs-navigation {
  display: flex;
  gap: 4px;
  padding: 0 28px;
  background: var(--surface);
  border-bottom: 1px solid var(--border-light);
  overflow-x: auto;
  scrollbar-width: none;
  flex-shrink: 0;
}

.tabs-navigation::-webkit-scrollbar {
  display: none;
}

.tab-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 16px;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  color: var(--text-secondary);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition);
  white-space: nowrap;
  position: relative;
}

.tab-btn:hover {
  color: var(--primary);
}

.tab-btn.active {
  color: var(--primary);
  border-bottom-color: var(--primary);
}

.tab-btn .material-symbols-outlined {
  font-size: 18px;
}

.tab-label {
  display: none;
}

@media (min-width: 768px) {
  .tab-label {
    display: inline;
  }
}

.tab-indicator {
  position: absolute;
  top: 8px;
  right: 8px;
}

.tab-indicator .material-symbols-outlined {
  font-size: 12px;
  color: var(--success);
}

/* ============================================
   TAB CONTENT
   ============================================ */
.tab-content {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.tab-panel {
  padding: 24px 28px;
}

/* Tab transitions */
.tab-fade-enter-active,
.tab-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.tab-fade-enter,
.tab-fade-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

/* Slide fade transition */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.25s ease;
}

.slide-fade-enter,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* ============================================
   PANEL GRID
   ============================================ */
.panel-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;
}

@media (min-width: 768px) {
  .panel-grid {
    grid-template-columns: 1fr 1fr;
  }
}

.panel-column {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* ============================================
   FIELD GROUPS
   ============================================ */
.field-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-group.full-width {
  grid-column: 1 / -1;
}

.field-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--text-secondary);
}

.field-label .material-symbols-outlined {
  font-size: 16px;
  color: var(--accent);
}

.field-value {
  font-size: 0.9375rem;
  color: var(--text-primary);
  margin: 0;
}

.field-value.description {
  line-height: 1.6;
}

/* Logo Upload */
.logo-field {
  grid-column: 1 / -1;
}

.logo-upload-area {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.logo-preview {
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: var(--radius-md);
  overflow: hidden;
  border: 1px solid var(--border);
}

.logo-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.remove-logo-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--danger);
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.remove-logo-btn .material-symbols-outlined {
  font-size: 14px;
}

.logo-dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 120px;
  height: 80px;
  border: 2px dashed var(--border);
  border-radius: var(--radius-md);
  cursor: pointer;
  color: var(--text-muted);
  font-size: 0.8125rem;
  transition: var(--transition);
}

.logo-dropzone:hover {
  border-color: var(--primary);
  color: var(--primary);
  background: var(--primary-lighter);
}

.logo-dropzone .material-symbols-outlined {
  font-size: 24px;
}

/* Date Picker */
.date-picker-wrapper {
  position: relative;
}

.date-input {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  cursor: pointer;
  font-size: 0.9375rem;
  color: var(--text-primary);
  background: var(--surface);
  transition: var(--transition);
}

.date-input:hover {
  border-color: var(--primary);
}

.date-input .material-symbols-outlined {
  font-size: 18px;
  color: var(--text-muted);
}

/* ============================================
   INTEGRATION FLOW
   ============================================ */
.integration-flow {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.integration-step {
  padding: 20px;
  background: var(--surface-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  transition: var(--transition);
}

.integration-step.completed {
  border-color: var(--accent);
  background: var(--primary-lighter);
}

.step-header {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 16px;
}

.step-number {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--border);
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8125rem;
  font-weight: 600;
}

.integration-step.completed .step-number {
  background: var(--primary);
  color: white;
}

.step-info h3 {
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 2px;
}

.step-info p {
  font-size: 0.8125rem;
  color: var(--text-muted);
  margin: 0;
}

.empty-integration {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 48px 24px;
  color: var(--text-muted);
  text-align: center;
}

.empty-integration .material-symbols-outlined {
  font-size: 48px;
  opacity: 0.5;
}

/* ============================================
   RELATIONS
   ============================================ */
.relations-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
}

@media (min-width: 768px) {
  .relations-grid {
    grid-template-columns: 1fr 1fr;
  }
}

.type-badge {
  display: inline-flex;
  padding: 8px 16px;
  background: var(--primary-lighter);
  color: var(--primary);
  border-radius: var(--radius-sm);
  font-size: 0.875rem;
  font-weight: 500;
}

.relation-card {
  padding: 20px;
  background: var(--surface-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
}

.relation-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}

.relation-header .material-symbols-outlined {
  font-size: 18px;
  color: var(--accent);
}

.relation-header h4 {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.relation-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.relation-chip {
  display: inline-flex;
  padding: 6px 12px;
  background: var(--primary);
  color: white;
  border-radius: var(--radius-sm);
  font-size: 0.8125rem;
  font-weight: 500;
}

.empty-text {
  font-size: 0.8125rem;
  color: var(--text-muted);
  font-style: italic;
}

/* ============================================
   OPTIONS
   ============================================ */
.options-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.option-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  background: var(--surface-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  transition: var(--transition);
}

.option-card.enabled {
  border-color: var(--accent);
  background: var(--primary-lighter);
}

.option-card.sub-option {
  margin-left: 32px;
}

.option-card.large {
  padding: 24px;
}

.option-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.option-icon {
  width: 44px;
  height: 44px;
  border-radius: var(--radius-sm);
  background: var(--surface);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--accent);
}

.option-icon.warning {
  color: var(--warning);
  background: var(--warning-light);
}

.option-icon .material-symbols-outlined {
  font-size: 22px;
}

.option-info h4 {
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 4px;
}

.option-info p {
  font-size: 0.8125rem;
  color: var(--text-muted);
  margin: 0;
}

/* Climate Params */
.climate-params {
  padding: 20px;
  background: var(--surface-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  margin-left: 32px;
}

.params-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.param-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.param-field label {
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--text-secondary);
}

/* ============================================
   DATA SECTIONS
   ============================================ */
.data-sections {
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.data-section {
  padding: 0;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}

.section-header .material-symbols-outlined {
  font-size: 20px;
  color: var(--accent);
}

.section-header h3 {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.formula-input-wrapper {
  margin-top: 8px;
}

.formula-input {
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 0.875rem;
}

.formula-display {
  display: block;
  padding: 12px 16px;
  background: var(--surface-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 0.875rem;
  color: var(--text-primary);
}

/* ============================================
   TIMESTAMP CONFIG
   ============================================ */
.timestamp-config {
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.timestamp-defaults {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
}

@media (min-width: 768px) {
  .timestamp-defaults {
    grid-template-columns: 1fr 1fr;
  }
}

.overrides-section {
  background: var(--surface-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  overflow: hidden;
}

.overrides-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: var(--surface);
  border-bottom: 1px solid var(--border);
}

.overrides-header h3 {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.overrides-header .material-symbols-outlined {
  font-size: 18px;
  color: var(--accent);
}

.overrides-table {
  padding: 16px 20px;
}

.override-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 40px;
  gap: 12px;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid var(--border-light);
}

.override-row:last-child {
  border-bottom: none;
}

.override-row.header {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding-bottom: 12px;
}

.empty-overrides {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 40px 20px;
  color: var(--text-muted);
}

.empty-overrides .material-symbols-outlined {
  font-size: 36px;
  opacity: 0.5;
}

/* ============================================
   USERS
   ============================================ */
.users-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.users-selector {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-start;
}

.users-selector .custom-autocomplete {
  flex: 1;
  min-width: 280px;
}

.users-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
}

.user-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: var(--surface-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--primary);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8125rem;
  font-weight: 600;
  flex-shrink: 0;
}

.user-info {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.user-name {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-role {
  font-size: 0.75rem;
  color: var(--text-muted);
  text-transform: capitalize;
}

.empty-users {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 48px 24px;
  color: var(--text-muted);
}

.empty-users .material-symbols-outlined {
  font-size: 48px;
  opacity: 0.5;
}

/* User Dialog */
.user-dialog-card .dialog-header {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--primary);
  color: white;
}

/* ============================================
   CO2 SECURITY
   ============================================ */
.co2-config {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.co2-details {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.sensor-config-table {
  background: var(--surface-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  overflow: hidden;
}

.table-header {
  display: grid;
  grid-template-columns: 1fr 1fr 100px 100px;
  gap: 12px;
  padding: 14px 20px;
  background: var(--surface);
  border-bottom: 1px solid var(--border);
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.table-row {
  display: grid;
  grid-template-columns: 1fr 1fr 100px 100px;
  gap: 12px;
  padding: 12px 20px;
  align-items: center;
  border-bottom: 1px solid var(--border-light);
}

.table-row:last-child {
  border-bottom: none;
}

.sensor-name {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
}

/* ============================================
   FTP CONFIG
   ============================================ */
.ftp-config {
  min-height: 200px;
}

.ftp-notice {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px;
  background: var(--primary-lighter);
  border: 1px solid var(--accent);
  border-radius: var(--radius-md);
}

.ftp-notice .material-symbols-outlined {
  font-size: 24px;
  color: var(--primary);
  flex-shrink: 0;
}

.ftp-notice h4 {
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 4px;
}

.ftp-notice p {
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin: 0;
}

/* ============================================
   FOOTER
   ============================================ */
.form-footer {
  padding: 16px 28px;
  background: var(--surface);
  border-top: 1px solid var(--border);
  flex-shrink: 0;
}

.footer-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.footer-left {
  display: flex;
  align-items: center;
}

.save-hint {
  display: none;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  color: var(--text-muted);
}

@media (min-width: 768px) {
  .save-hint {
    display: flex;
  }
}

.save-hint .material-symbols-outlined {
  font-size: 14px;
}

.footer-actions {
  display: flex;
  gap: 10px;
}

.footer-actions .btn-ghost {
  background: var(--surface-elevated);
  color: var(--text-secondary);
  border: 1px solid var(--border);
}

.footer-actions .btn-ghost:hover {
  background: var(--border-light);
}

/* ============================================
   VUETIFY OVERRIDES
   ============================================ */
.custom-autocomplete >>> .v-input__slot {
  background: var(--surface) !important;
  border: 1px solid var(--border) !important;
  border-radius: var(--radius-sm) !important;
  box-shadow: none !important;
  min-height: 44px !important;
}

.custom-autocomplete >>> .v-input__slot:hover {
  border-color: var(--primary) !important;
}

.custom-autocomplete >>> .v-input--is-focused .v-input__slot {
  border-color: var(--primary) !important;
  box-shadow: 0 0 0 3px var(--primary-lighter) !important;
}

.custom-autocomplete >>> .v-select__selections {
  padding: 4px 8px !important;
}

.custom-autocomplete >>> .v-chip {
  background: var(--primary) !important;
  color: white !important;
  font-size: 0.8125rem !important;
}

.custom-autocomplete >>> .v-chip .v-chip__close {
  color: rgba(255, 255, 255, 0.7) !important;
}

.chips-input >>> .v-select__selections {
  gap: 6px;
}

/* Switch overrides */
>>> .v-input--switch .v-input--switch__track {
  opacity: 0.4;
}

>>> .v-input--switch.v-input--is-label-active .v-input--switch__track {
  opacity: 0.7;
}

/* ============================================
   RESPONSIVE
   ============================================ */
@media (max-width: 640px) {
  .form-header .header-content {
    padding: 16px 20px;
  }

  .quick-edit-section,
  .tabs-navigation,
  .tab-panel,
  .form-footer {
    padding-left: 20px;
    padding-right: 20px;
  }

  .form-title {
    font-size: 1.125rem;
  }

  .site-avatar {
    width: 44px;
    height: 44px;
  }

  .option-card.sub-option,
  .climate-params {
    margin-left: 16px;
  }
}

>>> .v-input--selection-controls {
  margin: 0;
  padding: 0;
}

/* ============================================
   MATERIAL SYMBOLS
   ============================================ */
.material-symbols-outlined {
  font-family: 'Material Symbols Outlined';
  font-variation-settings:
      'FILL' 0,
      'wght' 400,
      'GRAD' 0,
      'opsz' 24;
}
</style>